import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Crown, Zap, ArrowRight, Loader2, ExternalLink } from "lucide-react";
import { toast, Toaster } from "sonner";

import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { api } from "@/lib/api";
import ThemeToggle from "@/components/ThemeToggle";
import Footer from "@/components/Footer";

const FEATURE_LABELS = {
  multi_business: "Più attività gestite in un unico account",
  extra_cards: "RecenUp Card illimitate",
  advanced_analytics: "Analytics avanzati (funnel, cohort, benchmark)",
  per_card_analytics: "Analytics per singola card e location",
  collaborators: "Fino a 5 collaboratori",
  ai_insights: "AI Insights — sentiment, forze, criticità",
  ai_reply: "Risposte AI multilingua alle recensioni",
  multilocation_gbp: "Sincronizzazione multi-location Google Business",
  advanced_reports: "Report avanzati esportabili",
};

const FREE_ROWS = [
  "1 attività",
  "1 RecenUp Card",
  "1 Google Business Profile collegato",
  "QR + NFC funzionanti",
  "Dashboard base + conteggio tap",
  "Link recensione Google gestito",
];

const euro = (n) => `€ ${Number(n || 0).toFixed(2).replace(".", ",")}`;

export default function Pricing() {
  const { user, isPro, refresh } = useAuth();
  const { theme } = useTheme();
  const [params] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/plan/config");
        setConfig(data);
      } catch {
        toast.error("Errore nel caricamento dei prezzi");
      } finally {
        setLoading(false);
      }
    })();
    if (params.get("upgraded") === "1") {
      toast.success("Pagamento ricevuto — sto attivando Pro…", { duration: 6000 });
      // Refresh a few times because webhook may take a moment
      const timers = [1200, 3000, 6000].map((ms) => setTimeout(() => refresh(), ms));
      return () => timers.forEach(clearTimeout);
    }
    if (params.get("canceled") === "1") {
      toast.info("Nessun addebito — puoi riprovare quando vuoi.");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upgrade = async () => {
    if (!user) {
      window.location.href = "/";
      return;
    }
    setUpgrading(true);
    try {
      const { data } = await api.post("/billing/checkout/pro", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore avvio checkout");
      setUpgrading(false);
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore apertura portale");
      setPortalLoading(false);
    }
  };

  const proPrice = config?.pro_price_eur ?? 20;
  const proFeatures = Object.entries(FEATURE_LABELS);

  return (
    <div className="min-h-screen bg-[var(--bg-1)]" data-testid="pricing-page">
      <Toaster position="top-right" theme={theme} />

      <header className="sticky top-0 z-40 bg-[var(--glass-bg)] backdrop-blur-xl border-b border-[var(--border-c)]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="font-serif text-3xl text-[var(--fg-1)] tracking-tight leading-none">
            Recen<span className="italic text-[#00F2F4]">Up</span>
          </Link>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            {user ? (
              <Link to="/dashboard" className="text-xs uppercase tracking-[0.2em] text-[var(--fg-3)] hover:text-[var(--fg-1)]" style={{ transition: "color 0.2s ease" }}>
                ← Dashboard
              </Link>
            ) : (
              <Link to="/" className="text-xs uppercase tracking-[0.2em] text-[var(--fg-3)] hover:text-[var(--fg-1)]" style={{ transition: "color 0.2s ease" }}>
                ← Home
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-14">
        <div className="text-center mb-14">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#00F2F4] font-sans font-semibold">
            Piani RecenUp
          </div>
          <h1 className="mt-3 font-serif text-5xl md:text-6xl text-[var(--fg-1)] tracking-tight">
            Comincia gratis. <span className="italic">Cresci</span> con Pro.
          </h1>
          <p className="mt-4 text-base text-[var(--fg-3)] max-w-xl mx-auto">
            Nessun contratto. Nessun costo nascosto. Puoi cambiare piano quando vuoi.
          </p>
        </div>

        {loading ? (
          <div className="p-10 text-center text-sm text-[var(--fg-3)]">Caricamento prezzi…</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6" data-testid="pricing-cards">
            {/* FREE */}
            <div
              data-testid="pricing-free"
              className="rounded-2xl border border-[var(--border-c)] bg-[var(--bg-2)] p-8 shadow-[0_2px_20px_rgba(0,0,0,0.20)]"
            >
              <div className="flex items-center gap-2 text-[var(--fg-2)]">
                <Zap size={14} strokeWidth={2.2} />
                <span className="text-[10px] uppercase tracking-[0.25em] font-sans font-semibold">Free</span>
              </div>
              <div className="mt-4 font-serif text-5xl text-[var(--fg-1)] leading-none">€ 0</div>
              <div className="mt-1 text-xs text-[var(--fg-3)] font-sans">per sempre</div>
              <ul className="mt-8 space-y-3">
                {FREE_ROWS.map((row) => (
                  <li key={row} className="flex items-start gap-3 text-sm text-[var(--fg-2)]">
                    <Check size={14} className="text-[#00F2F4] mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                    <span>{row}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                {user ? (
                  <div className="text-xs text-[var(--fg-3)] font-sans">
                    {isPro ? "Puoi tornare a Free cancellando l'abbonamento Pro." : "Sei sul piano Free."}
                  </div>
                ) : (
                  <Link
                    to="/"
                    data-testid="pricing-free-cta"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-[var(--border-c)] hover:border-[var(--fg-2)] text-[var(--fg-1)] px-5 py-3 text-sm font-sans"
                    style={{ transition: "border-color 0.2s ease" }}
                  >
                    Inizia gratis <ArrowRight size={14} />
                  </Link>
                )}
              </div>
            </div>

            {/* PRO */}
            <div
              data-testid="pricing-pro"
              className="relative rounded-2xl border-2 border-[#00F2F4]/60 bg-[var(--bg-2)] p-8 shadow-[0_2px_32px_rgba(0,242,244,0.10)] overflow-hidden"
            >
              <div className="absolute top-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-[#00F2F4]/12 text-[#00F2F4] px-3 py-1 text-[10px] font-sans font-semibold uppercase tracking-[0.18em]">
                <Crown size={11} strokeWidth={2.2} /> Consigliato
              </div>
              <div className="flex items-center gap-2 text-[#00F2F4]">
                <Crown size={14} strokeWidth={2.2} />
                <span className="text-[10px] uppercase tracking-[0.25em] font-sans font-semibold">Pro</span>
              </div>
              <div className="mt-4 flex items-baseline gap-1.5">
                <div className="font-serif text-5xl text-[var(--fg-1)] leading-none" data-testid="pricing-pro-price">
                  {euro(proPrice)}
                </div>
                <div className="text-sm text-[var(--fg-3)] font-sans">/mese</div>
              </div>
              <div className="mt-1 text-xs text-[var(--fg-3)] font-sans">Fatturazione mensile · cancellazione in un click</div>
              <ul className="mt-8 space-y-3">
                <li className="flex items-start gap-3 text-sm text-[var(--fg-2)]">
                  <Check size={14} className="text-[#00F2F4] mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  <span className="font-semibold">Tutto quanto c'è in Free, più:</span>
                </li>
                {proFeatures.map(([key, label]) => (
                  <li key={key} className="flex items-start gap-3 text-sm text-[var(--fg-2)]">
                    <Check size={14} className="text-[#00F2F4] mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                    <span>{label}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8 space-y-3">
                {!isPro ? (
                  <button
                    onClick={upgrade}
                    disabled={upgrading || !config?.stripe_configured}
                    data-testid="pricing-pro-upgrade-btn"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#00F2F4] hover:bg-[#00D6D8] text-[#0A0A0A] px-5 py-3 text-sm font-sans font-semibold disabled:opacity-60"
                    style={{ transition: "background-color 0.2s ease" }}
                  >
                    {upgrading ? <Loader2 size={14} className="animate-spin" /> : <Crown size={14} />}
                    {upgrading ? "Reindirizzamento a Stripe…" : `Passa a Pro · ${euro(proPrice)}/mese`}
                  </button>
                ) : (
                  <button
                    onClick={openPortal}
                    disabled={portalLoading}
                    data-testid="pricing-pro-portal-btn"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#00F2F4] hover:bg-[#00D6D8] text-[#0A0A0A] px-5 py-3 text-sm font-sans font-semibold disabled:opacity-60"
                    style={{ transition: "background-color 0.2s ease" }}
                  >
                    {portalLoading ? <Loader2 size={14} className="animate-spin" /> : <ExternalLink size={14} />}
                    {portalLoading ? "Apertura portale…" : "Gestisci abbonamento"}
                  </button>
                )}
                {!config?.stripe_configured && !isPro && (
                  <div className="text-xs text-[var(--fg-3)] italic text-center" data-testid="pricing-stripe-not-ready">
                    Stripe non ancora configurato — chiedi all'admin di eseguire il bootstrap.
                  </div>
                )}
                <div className="text-[10px] text-center text-[var(--fg-3)] uppercase tracking-[0.18em] font-sans">
                  Pagamento sicuro via Stripe · Carta / Apple Pay / Google Pay
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-16 text-center text-xs text-[var(--fg-3)] max-w-2xl mx-auto">
          Le funzioni AI (sentiment, risposta) saranno attivate al collegamento di Gemini nella prossima fase. Il piano Pro è già disponibile: prenoti il posto e attivi tutto in un click.
        </div>
      </main>
      <Footer />
    </div>
  );
}
